import type { EsgDataQueryParams, EsgDataResponse } from './types';

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { queryKeysAnalytics } from '../queryKeys';

import { fetchEsgData } from './fetchEsgData';

/**
 * Generates the query options for the ESG data query so they can be shared
 * between the useQuery hook and prefetching.
 *
 * @param queryParams - The query parameters for fetching the ESG data.
 * @param options - Additional options for the query.
 *   Use this for prefetching in route loaders to prevent request cancellation when the loader completes.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getEsgDataQueryOptions = ({
  queryParams,
  options,
}: {
  queryParams: EsgDataQueryParams;
  options?: Omit<
    UseQueryOptions<EsgDataResponse, Error, EsgDataResponse>,
    'queryKey' | 'queryFn'
  >;
}) => {
  return {
    queryFn: async () => fetchEsgData(queryParams),
    queryKey: queryKeysAnalytics.getEsgData(queryParams),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the ESG data with given query parameters.
 * @param queryParams - The query parameters for fetching the ESG data.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook containing ESG data with hierarchy for row grouping.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note-Items/operation/esg_values_asset_delivery_note_items_esg_values_get
 */
export const useQueryEsgData = (
  queryParams: EsgDataQueryParams,
  options?: Omit<
    UseQueryOptions<EsgDataResponse, Error, EsgDataResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getEsgDataQueryOptions({
      options,
      queryParams,
    }),
  });
